import { Box, Button, Heading, HStack, Text } from '@chakra-ui/react'
import { Plus } from 'lucide-react'
import { memo } from 'react'

import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'
import { SwitchboardSearchBar } from './SwitchboardSearchBar'

export interface SwitchboardListHeaderProps {
  /** Current search term */
  searchTerm: string
  /** Callback when search term changes */
  onSearchChange: (value: string) => void
  /** Callback when "New Switchboard" button is clicked */
  onNewClick: () => void
  /** Optional title override */
  title?: string
  /** Optional subtitle override */
  subtitle?: string
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
}

/**
 * Header component for switchboard list page
 * Contains title, subtitle, search bar, and "New Switchboard" button
 */
export const SwitchboardListHeader = memo<SwitchboardListHeaderProps>(
  ({
    searchTerm,
    onSearchChange,
    onNewClick,
    title: titleProp,
    subtitle: subtitleProp,
    t,
  }) => {
    const title = titleProp ?? text(t, 'switchboard.list.title', 'Switchboard')
    const subtitle =
      subtitleProp ??
      text(
        t,
        'switchboard.list.subtitle',
        'Create and manage call flows for your voice system',
      )
    return (
      <Box
        display="flex"
        h="72px"
        px={5}
        justifyContent="space-between"
        alignItems="center"
        flex="1 0 0"
        mb={4}
      >
        {/* Title and Subtitle - hidden on screens smaller than md */}
        <Box display={{ base: 'none', md: 'block' }}>
          <Heading
            as="h1"
            fontSize="20px"
            fontWeight="extrabold"
            lineHeight="28px"
            color="fg.default"
            mb={1}
          >
            {title}
          </Heading>
          <Text fontSize="sm" color="fg.subtle">
            {subtitle}
          </Text>
        </Box>

        {/* Search and Button Container - full width + justify-between below md */}
        <HStack
          gap={6}
          justifyContent={{ base: 'space-between', md: 'flex-end' }}
          width={{ base: '100%', md: 'auto' }}
          flexShrink={{ base: 1, md: 0 }}
        >
          <SwitchboardSearchBar
            value={searchTerm}
            onChange={onSearchChange}
            t={t}
          />

          {/* New Switchboard Button */}
          <Button
            onClick={onNewClick}
            size="md"
            bg="blue.solid"
            color="blue.contrast"
            borderRadius="lg"
            flexShrink={0}
            whiteSpace="nowrap"
            _hover={{ bg: 'blue.emphasized' }}
            _active={{ bg: 'blue.emphasized' }}
            css={{ '& svg': { stroke: 'var(--chakra-colors-blue-contrast)' } }}
            px={4}
          >
            <Plus size={16} />
            {text(t, 'switchboard.list.newSwitchboard', 'New Switchboard')}
          </Button>
        </HStack>
      </Box>
    )
  },
)

SwitchboardListHeader.displayName = 'SwitchboardListHeader'
